
import React, { useState } from 'react';
import { WeeklyLog, StudentConnection } from '../types';
import { Avatar } from './common';
import { useCheckinTranslation } from '../hooks/useCheckinTranslation';

export const StatsView = ({ students, logs, academicYearStartDate, t }: {
    students: StudentConnection[],
    logs: WeeklyLog[],
    academicYearStartDate: Date,
    t: (key: string, reps?: any) => string
}) => {
    const [viewMode, setViewMode] = useState<'pulse' | 'participation'>('pulse');
    const approvedStudents = students.filter(s => s.approvalStatus === 'approved');

    if (approvedStudents.length === 0) {
        return <div className="p-4 text-center text-gray-500">{t('no_approved_students')}</div>;
    }

    const now = new Date();
    const weeksPassed = Math.max(1, Math.ceil((now.getTime() - academicYearStartDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));

    // --- PULSE VIEW HELPERS ---
    const getResponseCounts = (key: keyof WeeklyLog) => {
        const counts: Record<string, number> = {};
        logs.forEach(log => {
            const response = log[key] as string;
            if (response) {
                counts[response] = (counts[response] || 0) + 1;
            }
        });
        return counts;
    };

    const renderDynamicBar = (counts: Record<string, number>, total: number) => {
        if (total === 0) return <div className="h-4 bg-gray-100 rounded-full w-full"></div>;

        const colors = [
            'bg-indigo-500', 'bg-blue-400', 'bg-teal-400', 'bg-green-400', 
            'bg-yellow-400', 'bg-orange-400', 'bg-red-400', 'bg-pink-400'
        ];
        
        const keys = Object.keys(counts);
        
        return (
            <div className="space-y-2 mt-3">
                <div className="h-4 w-full flex rounded-full overflow-hidden shadow-inner bg-gray-100">
                    {keys.map((key, idx) => {
                        const width = (counts[key] / total) * 100;
                        return (
                            <div 
                                key={key} 
                                style={{ width: `${width}%` }} 
                                className={`${colors[idx % colors.length]}`} 
                                title={`${key}: ${counts[key]}`}
                            />
                        );
                    })}
                </div>
                <div className="flex flex-wrap gap-3">
                    {keys.map((key, idx) => (
                        <div key={key} className="flex items-center gap-1.5">
                            <div className={`w-2.5 h-2.5 rounded-full ${colors[idx % colors.length]}`}></div>
                            <span className="text-xs text-gray-600 truncate max-w-[150px]" title={key}>
                                {key} <span className="font-bold">({counts[key]})</span>
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const questions = [
        { key: 'studyStress', label: t('q_study_stress') },
        { key: 'understanding', label: t('q_understanding') },
        { key: 'physicalActivity', label: t('q_physical_activity') },
        { key: 'screenTime', label: t('q_screen_time') },
        { key: 'dailyRoutine', label: t('q_daily_routine') },
        { key: 'socialEmotional', label: t('q_social_emotional') },
        { key: 'needsSupport', label: t('q_needs_support') },
    ];

    // --- PARTICIPATION VIEW HELPERS ---
    const studentParticipation = approvedStudents.map(student => {
        const studentLogCount = logs.filter(l => l.studentId === student.id).length;
        const percentage = Math.min(100, (studentLogCount / weeksPassed) * 100);
        return {
            ...student,
            logCount: studentLogCount,
            percentage
        };
    }).sort((a, b) => a.percentage - b.percentage);

    return (
        <div className="p-4 space-y-4">
            <div className="flex bg-white rounded-xl shadow-sm p-1 border">
                <button 
                    onClick={() => setViewMode('pulse')}
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${viewMode === 'pulse' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    Class Pulse
                </button>
                <button 
                    onClick={() => setViewMode('participation')}
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${viewMode === 'participation' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    Participation
                </button>
            </div>

            {viewMode === 'pulse' && (
                <div className="space-y-4">
                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 text-indigo-800 text-sm flex items-center gap-2">
                        <span className="material-icons-round text-base">info</span>
                        <span>Displaying aggregated data from <strong>{logs.length}</strong> total check-ins.</span>
                    </div>

                    {questions.map((q) => {
                        const counts = getResponseCounts(q.key as keyof WeeklyLog);
                        const totalResponses = Object.values(counts).reduce((a, b) => a + b, 0);
                        
                        return (
                            <div key={q.key} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                                <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{q.label}</h4>
                                {renderDynamicBar(counts, totalResponses)}
                            </div>
                        );
                    })}
                </div>
            )}

            {viewMode === 'participation' && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                        <h3 className="font-bold text-gray-800">Submission Consistency</h3>
                        <span className="text-xs font-medium bg-white px-2 py-1 rounded border">Total Weeks: {weeksPassed}</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {studentParticipation.map(student => {
                            let barColor = 'bg-green-500';
                            if (student.percentage < 50) barColor = 'bg-red-500';
                            else if (student.percentage < 80) barColor = 'bg-yellow-500';

                            return (
                                <div key={student.id} className="p-4 flex items-center gap-3">
                                    <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                                    <div className="flex-1">
                                        <div className="flex justify-between mb-1">
                                            <span className="font-bold text-sm text-gray-800">{student.childName}</span>
                                            <span className="text-xs font-bold text-gray-500">
                                                {student.logCount} / {weeksPassed} ({Math.round(student.percentage)}%)
                                            </span>
                                        </div>
                                        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full rounded-full ${barColor}`} 
                                                style={{ width: `${student.percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export const ParentCheckinHistoryView = ({ 
    logs, 
    t, 
    userLanguage = 'en' 
}: { 
    logs: WeeklyLog[], 
    t: (key: string, reps?: any) => string,
    userLanguage?: string 
}) => {
    const { tCheckin, getTranslatedOption } = useCheckinTranslation(userLanguage);
    const recentLogs = [...logs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);

    if (recentLogs.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500 bg-white rounded-xl shadow-sm border m-4">
                <span className="material-icons-round text-4xl opacity-50 mb-2">history</span>
                <p>{tCheckin('no_checkins_yet', 'No check-ins submitted yet.')}</p>
            </div>
        );
    }

    const questions: { key: keyof WeeklyLog, label: string }[] = [
        { key: 'studyStress', label: 'q_study_stress' },
        { key: 'understanding', label: 'q_understanding' },
        { key: 'physicalActivity', label: 'q_physical_activity' },
        { key: 'screenTime', label: 'q_screen_time' },
        { key: 'dailyRoutine', label: 'q_daily_routine' },
        { key: 'socialEmotional', label: 'q_social_emotional' },
        { key: 'needsSupport', label: 'q_needs_support' },
    ];

    return (
        <div className="space-y-4 p-4">
            <h3 className="font-bold text-lg text-gray-800">{tCheckin('recent_checkins', 'Recent Check-ins')}</h3>
            {recentLogs.map(log => (
                <div key={log.id} className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="flex justify-between items-center mb-3 border-b border-gray-100 pb-2">
                        <h4 className="font-bold text-indigo-600">{new Date(log.date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</h4>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full font-medium">
                            {tCheckin('week_of', 'Week of')} {new Date(log.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                    </div>
                    <div className="space-y-3">
                        {questions.map(q => {
                            const val = log[q.key as keyof WeeklyLog] as string;
                            if (!val) return null;
                            
                            return (
                                <div key={q.key} className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm border-b border-gray-50 last:border-0 pb-1 last:pb-0">
                                    <span className="text-gray-500 font-medium mb-1 sm:mb-0">{tCheckin(q.label)}</span>
                                    <div className="font-bold text-gray-800 bg-gray-50 px-2 py-1 rounded text-right sm:text-left self-start sm:self-auto">
                                        {getTranslatedOption(val)}
                                    </div>
                                </div>
                            );
                        })}
                        {log.details && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <p className="text-xs text-gray-400 uppercase font-bold mb-1">{tCheckin('additional_details')}</p>
                                <p className="text-sm text-gray-700 italic bg-gray-50 p-2 rounded-lg">"{log.details}"</p>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
